import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ColorPicker } from "../../../right-panel/styling-panel/styling-color-picker";
import { QuickSwatch, QuickTip, QuickToggle, STEPPER_COMMIT_DELAY_MS, useQuickOpenState } from "./quick-controls";
import { IDebouncedCommit, useDebouncedCommit } from "./use-debounced-commit";

export interface IQuickColorButtonProps {
  color: string | null;
  label: string;
  testId: string;
  onCommit: (color: string) => void;
}

interface IClickPosition {
  x: number;
  y: number;
}

/**
 * A swatch that opens the styling panel's own colour picker (saved colours, eyedropper, alpha) at the
 * click, portalled to `document.body` — the picker positions itself `fixed`, which an animated ancestor
 * would otherwise capture. Dragging through colours commits once per pause, not once per frame.
 */
export const QuickColorButton = ({ color, label, testId, onCommit }: IQuickColorButtonProps): JSX.Element => {
  const [position, setPosition] = useState<IClickPosition | null>(null);
  // Shared with every other bar popover: opening another one (or a dismiss) closes this picker.
  const [isOpenKey, setIsOpenKey] = useQuickOpenState();
  const debounced: IDebouncedCommit<string> = useDebouncedCommit<string>(onCommit, STEPPER_COMMIT_DELAY_MS);
  const isOpen: boolean = isOpenKey && position !== null;

  const close = (): void => {
    debounced.flush();
    setPosition(null);
    setIsOpenKey(false);
  };

  // Closed from outside (another bar popover opened, a canvas click, a drag start): like Cancel, the
  // colour picked so far lands.
  useEffect((): void => {
    if (!isOpenKey) {
      debounced.flush();
      setPosition(null);
    }
  }, [isOpenKey, debounced]);

  return (
    <>
      <QuickTip label={label} isSuppressed={isOpen}>
        <QuickToggle
          isOn={isOpen}
          label={label}
          testId={testId}
          onMouseDown={(event: React.MouseEvent<HTMLButtonElement>): void => {
            // The picker closes itself on a mousedown outside it; this button toggles it instead.
            if (isOpen) {
              event.stopPropagation();
            }
          }}
          onToggle={(event: React.MouseEvent<HTMLButtonElement>): void => {
            if (isOpen) {
              close();
              return;
            }
            const rect: DOMRect = event.currentTarget.getBoundingClientRect();
            setPosition({ x: rect.left, y: rect.bottom + 8 });
            setIsOpenKey(true);
          }}
        >
          <QuickSwatch color={color} />
        </QuickToggle>
      </QuickTip>
      {isOpen &&
        createPortal(
          <div className="kuika_colorPicker" data-testid={`quickPropPopover-${testId.replace(/^quickProp-/, "")}`}>
            <ColorPicker
              color={color ?? undefined}
              isColorPickerActive
              hideHeaderTabs
              clickPosition={position ?? undefined}
              onChange={(next: string): void => debounced.schedule(next)}
              onCancel={close}
            />
          </div>,
          document.body
        )}
    </>
  );
};
